import { useInfiniteQuery } from '@tanstack/react-query';
import { findActionIn, invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
const DEFAULT_PAGE_SIZE = 50;
/**
 * Paginated list of agents that *can* be assigned to a given project (i.e.
 * agents not already assigned and not deactivated). Reads the per-record
 * `listPotentialAssignees` action from the project's `potentialAction`. The
 * action itself carries the `excludeDeactivated` and
 * `excludeAssigneesOfProjectUUID` values as readonly preset properties; we
 * read them off the action and pass them through to materialize the URL.
 */
export function useListPotentialAssignees({ project, searchTerm, status }) {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const action = findActionIn(project.potentialAction, 'listPotentialAssignees');
    const projectUUID = project.UUID || '';
    const sendSearch = searchTerm.length >= 2 ? searchTerm : '';
    const sendStatus = status === 'all' ? '' : status;
    return useInfiniteQuery({
        queryKey: ['listPotentialAssignees', user?.email, projectUUID, sendSearch, sendStatus],
        enabled: Boolean(user && action),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const view = lastPage.response.filteredView;
            const total = view?.numberOfItems ?? 0;
            const pageSize = view?.pageSize ?? DEFAULT_PAGE_SIZE;
            const current = view?.currentPage ?? lastPage.page;
            if (current * pageSize >= total)
                return undefined;
            return current + 1;
        },
        queryFn: async ({ pageParam }) => {
            if (!user || !action)
                throw new Error('listPotentialAssignees unavailable');
            // Readonly preset fields baked into the action — pass them through verbatim.
            const excludeDeactivated = typeof action.excludeDeactivated === 'string'
                ? action.excludeDeactivated
                : 'True';
            const excludeAssigneesOfProjectUUID = typeof action.excludeAssigneesOfProjectUUID === 'string'
                ? action.excludeAssigneesOfProjectUUID
                : projectUUID;
            const variables = {
                currentPage: String(pageParam),
                pageSize: String(DEFAULT_PAGE_SIZE),
                excludeDeactivated,
                excludeAssigneesOfProjectUUID,
            };
            if (sendSearch)
                variables.searchTerm = sendSearch;
            if (sendStatus)
                variables.status = sendStatus;
            const response = await invokeAction(action, user.token, { variables });
            if (!response.ok)
                throw new Error(`listPotentialAssignees returned ${response.status}`);
            const json = (await response.json());
            return { response: json, page: pageParam };
        },
    });
}
